import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Phone, Mail, MapPin, Building2, Home, CheckCircle, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ViewLeadDetails() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);
  const [contactRevealed, setContactRevealed] = useState(false);

  useEffect(() => {
    fetchLeadDetails();
  }, [matchId]);

  const fetchLeadDetails = async () => {
    try {
      const response = await axios.get(`${API}/company-view-lead/${matchId}`);
      setLead(response.data.lead);
      setCompany(response.data.company);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching lead details:', err);
      setError('Unable to load lead information. Please check your link or contact support.');
      setLoading(false);
    }
  };

  const revealContactInfo = () => {
    setContactRevealed(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F7F7' }}>
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-r-transparent" style={{ borderColor: '#3b82f6' }}></div>
          <p className="mt-4 text-gray-600">Loading lead information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F7F7F7' }}>
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-5xl mb-4" style={{ color: '#3b82f6' }}>⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition"
            style={{ backgroundColor: '#3b82f6' }}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F7F7' }}>
      {/* Header */}
      <div style={{ backgroundColor: '#1E1E1E' }} className="text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="inline-block rounded-full p-4 mb-4" style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)' }}>
              <User className="h-12 w-12" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">
              New Lead: {lead?.name}
            </h1>
            <p className="text-gray-300 text-lg">
              {lead?.city} • {lead?.property_type?.replace('-', ' ').toUpperCase()}
            </p>
            {/* Rent Worth Badge */}
            {lead?.lead_type === 'rent_worth' && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: '#f59e0b' }}>
                <span className="text-white font-bold text-lg">💰 Rent Worth Lead</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Success Alert */}
        <div className="border-2 rounded-lg p-4 mb-6" style={{ backgroundColor: '#e0f2fe', borderColor: '#3b82f6' }}>
          <div className="flex items-start gap-3">
            <CheckCircle className="h-6 w-6 mt-0.5" style={{ color: '#3b82f6' }} />
            <div>
              <h3 className="font-bold mb-1" style={{ color: '#1E1E1E' }}>
                {lead?.lead_type === 'rent_worth' ? '💰 Rental Valuation Request' : '✅ Lead Details'}
              </h3>
              <p className="text-sm" style={{ color: '#1E1E1E' }}>
                {lead?.lead_type === 'rent_worth' 
                  ? `${lead?.name} is requesting a rental valuation quote for their property in ${lead?.city}. Provide a competitive quote and discuss management opportunities.`
                  : `New property management opportunity in ${lead?.city}. Click below to view contact information.`
                }
              </p>
            </div>
          </div>
        </div>

        {/* Company Info */}
        {company && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6 border-l-4" style={{ borderColor: '#3b82f6' }}>
            <p className="text-sm text-gray-600">
              This lead has been assigned to: <span className="font-bold text-gray-900">{company.company_name}</span>
            </p>
          </div>
        )}

        {/* Lead Contact Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
          <div className="text-white p-6" style={{ background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)' }}>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <User className="h-7 w-7" />
              Lead Information
            </h2>
          </div>

          <div className="p-6 space-y-6">
            {/* Name */}
            <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
              <User className="h-6 w-6 text-slate-600 mt-1" />
              <div>
                <div className="text-sm text-slate-500 font-semibold uppercase mb-1">Full Name</div>
                <div className="text-lg font-semibold text-slate-900">{lead?.name}</div>
              </div>
            </div>

            {/* Contact Info - Blurred until button clicked */}
            {!contactRevealed ? (
              <div className="relative">
                {/* Blurred Contact Preview */}
                <div className="filter blur-sm select-none pointer-events-none">
                  {/* Phone - Blurred */}
                  <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg border-2 border-green-200 mb-4">
                    <Phone className="h-6 w-6 text-green-600 mt-1" />
                    <div className="flex-1">
                      <div className="text-sm text-green-700 font-semibold uppercase mb-1">Phone Number</div>
                      <div className="text-xl font-bold text-green-600">(123) 456-7890</div>
                    </div>
                  </div>

                  {/* Email - Blurred */}
                  <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-200 mb-4">
                    <Mail className="h-6 w-6 text-blue-600 mt-1" />
                    <div className="flex-1">
                      <div className="text-sm text-blue-700 font-semibold uppercase mb-1">Email Address</div>
                      <div className="text-lg font-semibold text-blue-600">contact@example.com</div>
                    </div>
                  </div>

                  {/* Address - Blurred */}
                  <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                    <MapPin className="h-6 w-6 text-purple-600 mt-1" />
                    <div className="flex-1">
                      <div className="text-sm text-purple-700 font-semibold uppercase mb-1">Property Address</div>
                      <div className="text-lg font-semibold text-purple-600">123 Main Street, City, Postal</div>
                    </div>
                  </div>
                </div>

                {/* Reveal Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                  <div className="text-center">
                    <div className="inline-block p-6 rounded-lg shadow-xl" style={{ background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)' }}>
                      <h3 className="text-white text-xl font-bold mb-3">🔒 Contact Info Hidden</h3>
                      <p className="text-blue-100 text-sm mb-4">Click below to reveal phone, email & address</p>
                      <button
                        onClick={revealContactInfo}
                        className="bg-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition shadow-md"
                        style={{ color: '#3b82f6' }}
                      >
                        🔓 Reveal Contact Info
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Phone */}
                <div className="flex items-start gap-4 p-4 rounded-lg border-2 animate-fade-in" style={{ backgroundColor: '#e0f2fe', borderColor: '#3b82f6' }}>
                  <Phone className="h-6 w-6 mt-1" style={{ color: '#3b82f6' }} />
                  <div className="flex-1">
                    <div className="text-sm font-semibold uppercase mb-1" style={{ color: '#1e40af' }}>Phone Number</div>
                    <a 
                      href={`tel:${lead?.phone}`}
                      className="text-xl font-bold hover:underline"
                      style={{ color: '#3b82f6' }}
                    >
                      {lead?.phone}
                    </a>
                    <div className="mt-3">
                      <a
                        href={`tel:${lead?.phone}`}
                        className="inline-block text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition shadow-md"
                        style={{ backgroundColor: '#3b82f6' }}
                      >
                        📞 Call Now
                      </a>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4 p-4 rounded-lg border-2 animate-fade-in" style={{ backgroundColor: '#e0f2fe', borderColor: '#3b82f6' }}>
                  <Mail className="h-6 w-6 mt-1" style={{ color: '#3b82f6' }} />
                  <div className="flex-1">
                    <div className="text-sm font-semibold uppercase mb-1" style={{ color: '#1e40af' }}>Email Address</div>
                    <a 
                      href={`mailto:${lead?.email}`}
                      className="text-lg font-semibold hover:underline break-all"
                      style={{ color: '#3b82f6' }}
                    >
                      {lead?.email}
                    </a>
                    <div className="mt-3">
                      <a
                        href={`mailto:${lead?.email}`}
                        className="inline-block text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition shadow-md"
                        style={{ backgroundColor: '#3b82f6' }}
                      >
                        📧 Send Email
                      </a>
                    </div>
                  </div>
                </div>

                {/* Address - NOW SHOWN WHEN REVEALED */}
                {lead?.property_address && lead.property_address !== "Not provided" ? (
                  <div className="flex items-start gap-4 p-4 rounded-lg border-2 animate-fade-in" style={{ backgroundColor: '#e0f2fe', borderColor: '#3b82f6' }}>
                    <MapPin className="h-6 w-6 mt-1" style={{ color: '#3b82f6' }} />
                    <div className="flex-1">
                      <div className="text-sm font-semibold uppercase mb-1" style={{ color: '#1e40af' }}>Property Address</div>
                      <div className="text-lg font-semibold" style={{ color: '#1E1E1E' }}>{lead?.property_address}</div>
                      <div className="text-base mt-1" style={{ color: '#1e40af' }}>{lead?.city}, {lead?.postal_code}</div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-4 p-4 rounded-lg border-2 animate-fade-in" style={{ backgroundColor: '#f1f5f9', borderColor: '#cbd5e1' }}>
                    <MapPin className="h-6 w-6 mt-1" style={{ color: '#64748b' }} />
                    <div className="flex-1">
                      <div className="text-sm font-semibold uppercase mb-1" style={{ color: '#64748b' }}>Property Location</div>
                      <div className="text-lg font-semibold" style={{ color: '#1E1E1E' }}>{lead?.city}, {lead?.postal_code}</div>
                      <p className="text-sm text-slate-500 mt-1">Specific address will be discussed after initial contact</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Property Details Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
          <div className="text-white p-6" style={{ backgroundColor: '#1E1E1E' }}>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Building2 className="h-7 w-7" />
              Property Information
            </h2>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#F7F7F7' }}>
                <div className="text-sm text-slate-500 font-semibold uppercase mb-1">Property Type</div>
                <div className="text-base font-medium text-slate-900">{lead?.property_type?.replace('-', ' ').toUpperCase()}</div>
              </div>

              {lead?.num_units && lead.num_units > 1 && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#F7F7F7' }}>
                  <div className="text-sm text-slate-500 font-semibold uppercase mb-1">Number of Units</div>
                  <div className="text-base font-medium text-slate-900">{lead?.num_units}</div>
                </div>
              )}

              <div className="p-4 rounded-lg" style={{ backgroundColor: '#F7F7F7' }}>
                <div className="text-sm text-slate-500 font-semibold uppercase mb-1">Management Status</div>
                <div className="text-base font-medium text-slate-900">{lead?.management_status?.replace('-', ' ').toUpperCase()}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Tips */}
        <div className="border-2 rounded-lg p-6 mb-6" style={{ backgroundColor: '#fef3c7', borderColor: '#f59e0b' }}>
          <h3 className="text-lg font-bold mb-3" style={{ color: '#92400e' }}>⚡ Quick Action Tips</h3>
          <ul className="space-y-2 text-sm" style={{ color: '#92400e' }}>
            <li className="flex items-start gap-2">
              <span className="font-bold">1.</span>
              <span>{lead?.lead_type === 'rent_worth' ? 'Provide a competitive rental valuation quote within 24 hours' : 'Call or email the lead within the next 1-2 hours for best response rate'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">2.</span>
              <span>Introduce yourself and mention you received their inquiry through Top Prop</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">3.</span>
              <span>{lead?.lead_type === 'rent_worth' ? 'Use this as an opportunity to discuss full property management services' : 'Ask about their specific needs and timeline for property management'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">4.</span>
              <span>Schedule a property viewing or consultation call</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">5.</span>
              <span>Follow up within 24 hours if you don't hear back</span>
            </li>
          </ul>
        </div>

        {/* Support */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-2">Questions about this lead?</p>
          <a 
            href="mailto:info@topprop.ca" 
            className="text-green-600 hover:text-green-800 font-semibold hover:underline"
          >
            info@topprop.ca
          </a>
        </div>
      </div>
    </div>
  );
}
